// src/pages/admin/EmailTriggerManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import {
  ArrowLeft,
  Mail,
  CheckCircle,
  AlertTriangle,
  ToggleLeft,
  ToggleRight,
  Clock,
  ExternalLink,
  RefreshCw,
  Info,
} from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const BREVO_TEMPLATES_URL = 'https://app.brevo.com/email/template/list';

// ─── Sub-components ───────────────────────────────────────────────────────────

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  const colors =
    type === 'success'
      ? 'bg-green-50 border-green-200 text-green-800'
      : 'bg-red-50 border-red-200 text-red-800';
  const Icon = type === 'success' ? CheckCircle : AlertTriangle;

  return (
    <div
      className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg ${colors} max-w-sm`}
    >
      <Icon size={18} className="shrink-0" />
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-auto text-current opacity-60 hover:opacity-100">
        ✕
      </button>
    </div>
  );
}

function TriggerCard({ triggerKey, trigger, onToggle, toggling }) {
  const isToggling = toggling === triggerKey;

  const formattedDate = trigger.lastModifiedAt
    ? new Date(trigger.lastModifiedAt.seconds * 1000).toLocaleString('en-CA', {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null;

  return (
    <div
      className={`bg-white rounded-2xl border shadow-sm p-6 transition-all duration-200 ${
        trigger.enabled ? 'border-gray-200' : 'border-gray-100 opacity-75'
      }`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className={`p-2 rounded-xl shrink-0 ${
              trigger.enabled ? 'bg-green-50' : 'bg-gray-100'
            }`}
          >
            <Mail
              size={20}
              className={trigger.enabled ? 'text-green-600' : 'text-gray-400'}
            />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-800">
              {trigger.label}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">{trigger.description}</p>
          </div>
        </div>

        {/* Toggle */}
        <button
          onClick={() => onToggle(triggerKey, trigger.enabled)}
          disabled={isToggling}
          className="shrink-0 transition-opacity disabled:opacity-50"
          title={trigger.enabled ? 'Click to disable' : 'Click to enable'}
        >
          {isToggling ? (
            <RefreshCw size={32} className="text-gray-400 animate-spin" />
          ) : trigger.enabled ? (
            <ToggleRight size={40} className="text-green-500" />
          ) : (
            <ToggleLeft size={40} className="text-gray-300" />
          )}
        </button>
      </div>

      {/* Metadata row */}
      <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-4 text-xs text-gray-400">
        {/* Status badge */}
        <span
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-semibold border ${
            trigger.enabled
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-gray-100 text-gray-500 border-gray-200'
          }`}
        >
          {trigger.enabled ? (
            <CheckCircle size={11} />
          ) : (
            <AlertTriangle size={11} />
          )}
          {trigger.enabled ? 'Active' : 'Disabled'}
        </span>

        {/* Template ID */}
        <span className="flex items-center gap-1">
          <Info size={11} />
          Template #{trigger.templateId}
        </span>

        {/* Sender */}
        <span className="flex items-center gap-1">
          <Mail size={11} />
          {trigger.sender}
        </span>

        {/* Last modified */}
        {formattedDate && (
          <span className="flex items-center gap-1 ml-auto">
            <Clock size={11} />
            Last changed {formattedDate}
            {trigger.lastModifiedBy ? ` by ${trigger.lastModifiedBy}` : ''}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function EmailTriggerManager() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [triggers, setTriggers] = useState({});
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const hideToast = useCallback(() => setToast(null), []);

  // ── Load triggers from Firestore ──────────────────────────────────────────
  const loadTriggers = useCallback(async () => {
    setLoading(true);
    try {
      const configDoc = await getDoc(doc(db, 'config', 'emailTriggers'));
      if (configDoc.exists()) {
        setTriggers(configDoc.data());
      } else {
        setTriggers({});
      }
    } catch (err) {
      console.error('Failed to load email triggers:', err);
      showToast('Failed to load email triggers', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadTriggers();
  }, [loadTriggers]);

  // ── Toggle a trigger ──────────────────────────────────────────────────────
  const handleToggle = useCallback(
    async (triggerKey, currentEnabled) => {
      setToggling(triggerKey);
      const newEnabled = !currentEnabled;

      try {
        const configRef = doc(db, 'config', 'emailTriggers');

        // Use setDoc with merge in case document doesn't exist yet
        await setDoc(
          configRef,
          {
            [triggerKey]: {
              ...triggers[triggerKey],
              enabled: newEnabled,
              lastModifiedBy: user?.email || 'admin',
              lastModifiedAt: new Date(),
            },
          },
          { merge: true }
        );

        // Update local state
        setTriggers(prev => ({
          ...prev,
          [triggerKey]: {
            ...prev[triggerKey],
            enabled: newEnabled,
            lastModifiedBy: user?.email || 'admin',
            lastModifiedAt: { seconds: Math.floor(Date.now() / 1000) },
          },
        }));

        showToast(
          `${triggers[triggerKey]?.label || triggerKey} ${
            newEnabled ? 'enabled' : 'disabled'
          } successfully`
        );
      } catch (err) {
        console.error('Failed to toggle trigger:', err);
        showToast('Failed to update trigger — please try again', 'error');
      } finally {
        setToggling(null);
      }
    },
    [triggers, user, showToast]
  );

  // ── Derived data ──────────────────────────────────────────────────────────
  const triggerEntries = Object.entries(triggers).sort(([, a], [, b]) =>
    (a.label || '').localeCompare(b.label || '')
  );

  const activeCount = triggerEntries.filter(([, t]) => t.enabled).length;
  const totalCount = triggerEntries.length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}

      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 px-4 md:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Email Triggers
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Manage automated email notifications
              </p>
            </div>
          </div>

          {/* Brevo link */}
          <a
            href={BREVO_TEMPLATES_URL}
            target="_blank"
            rel="noreferrer"
            className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <ExternalLink size={15} />
            Brevo Templates
          </a>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">

        {/* Summary bar */}
        {!loading && totalCount > 0 && (
          <div className="flex items-center gap-6 mb-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
              <span className="text-sm text-gray-600">
                <span className="font-semibold text-gray-800">{activeCount}</span> active
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
              <span className="text-sm text-gray-600">
                <span className="font-semibold text-gray-800">
                  {totalCount - activeCount}
                </span>{' '}
                disabled
              </span>
            </div>
            <div className="ml-auto text-sm text-gray-400">
              {totalCount} trigger{totalCount !== 1 ? 's' : ''} total
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <RefreshCw size={28} className="animate-spin text-gray-400" />
          </div>
        )}

        {/* Empty state */}
        {!loading && totalCount === 0 && (
          <div className="text-center py-24">
            <Mail size={40} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-500 mb-1">
              No triggers configured
            </h3>
            <p className="text-sm text-gray-400">
              Add a trigger document to{' '}
              <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">
                config/emailTriggers
              </code>{' '}
              in Firestore to get started.
            </p>
          </div>
        )}

        {/* Trigger cards */}
        {!loading && totalCount > 0 && (
          <div className="flex flex-col gap-4">
            {triggerEntries.map(([key, trigger]) => (
              <TriggerCard
                key={key}
                triggerKey={key}
                trigger={trigger}
                onToggle={handleToggle}
                toggling={toggling}
              />
            ))}
          </div>
        )}

        {/* Info note */}
        {!loading && totalCount > 0 && (
          <p className="mt-8 text-xs text-gray-400 text-center">
            Toggling a trigger takes effect immediately — no redeployment needed.
            To edit template content, use Brevo's template editor.
          </p>
        )}
      </div>
    </div>
  );
}